"use client";

/**
 * Job-side bulk-action entry point into enrollment (spec §4a).
 *
 * Mounted by the Job Pipeline's bulk-action toolbar
 * (`features/jobs/components/view/pipeline/bulk-actions.tsx`) once one or
 * more candidates are checked. Clicking the button hydrates
 * `useConfigureStore` with the cohort + job context and opens the
 * Configure modal as a sibling — no portal plumbing through the toolbar.
 *
 * Visually it mirrors the existing primary CTA in the toolbar (Figma
 * `1678:895`): paper-plane icon + "Enroll in Sequence" label, primary
 * button variant. We render it via the shared `Button` component so the
 * spinner inset, focus ring, and disabled treatment match every other
 * toolbar action.
 */

import * as React from "react";

import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import ConfigureModal from "./configure-modal";
import { useConfigureStore } from "../store/configure-store";

/** Hard cap on a single enrollment cohort — matches the Configure review gate. */
const MAX_COHORT = 50;

export interface JobEnrollActionProps {
  /** Job UUID the cohort belongs to (`Sequence.job_id` constraint). */
  jobId: string;
  /** `job_candidates.id[]` to enroll. Empty disables the button. */
  jobCandidateIds: string[];
  /** Hard disable from caller (e.g. mid-bulk-move). Empty cohort also disables. */
  disabled?: boolean;
  /** Pass-through for toolbar layout overrides (`hidden md:inline-flex` etc). */
  className?: string;
}

export function JobEnrollAction({
  jobId,
  jobCandidateIds,
  disabled = false,
  className,
}: JobEnrollActionProps) {
  const setConfigure = useConfigureStore((s) => s.set);
  const [open, setOpen] = React.useState(false);

  const isEmpty = jobCandidateIds.length === 0;
  // Over the per-cohort cap the button is greyed out (the Configure review step
  // and the backend preview both reject >50, so we gate at the entry point too).
  const isOverCap = jobCandidateIds.length > MAX_COHORT;
  const isDisabled = disabled || isEmpty || isOverCap;

  const handleClick = React.useCallback(() => {
    if (isDisabled) return;
    setConfigure({
      jobId,
      jobCandidateIds,
      sequenceId: null,
    });
    setOpen(true);
  }, [isDisabled, jobId, jobCandidateIds, setConfigure]);

  const tooltipLabel = isOverCap
    ? `You can only enroll ${MAX_COHORT} profiles at a time.`
    : "Enroll in Sequence";

  return (
    <>
      <Tooltip>
        {/* Wrap the button so the tooltip still fires while it's disabled
            (disabled buttons get `pointer-events-none`, which swallows hover). */}
        <TooltipTrigger asChild>
          <span className={cn("inline-flex", className)}>
            <Button
              type="button"
              size="sm"
              variant="primary"
              icon={<Icon name="IconPaperPlane" />}
              disabled={isDisabled}
              onClick={handleClick}
            >
              <span className="hidden min-[1600px]:inline">
                Enroll in Sequence
              </span>
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className={cn(!isOverCap && "min-[1600px]:hidden")}
        >
          {tooltipLabel}
        </TooltipContent>
      </Tooltip>
      <ConfigureModal open={open} onOpenChange={setOpen} />
    </>
  );
}

export default JobEnrollAction;
